import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Building, FileText, MapPin, Users, Calendar, Target, Save, Loader,
  AlertCircle, Star, Award, UserCheck, Plus, Trash2, Brain, Clock,
  GraduationCap, Briefcase, CheckCircle, ExternalLink, Download, Zap, Loader2, AlertTriangle
} from 'lucide-react';
import { needsApi, usersApi, candidatesApi } from '../../services/api';
import { Modal, ModalActions, TagInput, Alert } from '../shared';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import { useSofteamboardStore, normalizeStep } from '../../store/softeamboardStore';
import FormSectionTabs from './FormSectionTabs';

const EditNeedModal = ({ 
  need, 
  open, 
  onClose, 
  onSuccess, 
  mode = 'normal', // 'normal' | 'post-analysis'
  missingFields = [],
  requiredForWorkflows = []
}) => {
  const [formData, setFormData] = useState({
    // Basic fields
    title: '',
    isSubmitting: false,
    client: '',
    priority: 'P2',
    column: 'NO_CANDIDATE',
    progressPct: 0,
    description: '',
    category: '',
    quantity: 1,
    
    // Commercial fields
    tjm: '',
    
    // ✅ UPDATED: Use URL instead of ID for opportunity
    opportunitySalesforceUrl: '',
    missionSalesforceUrl: '',
    
    // Order clients fields
    orderClients: [],
    orderClientsEmail: [],
    
    // JD Analysis fields - individual fields (preferred)
    jdSeniority: '',
    jdTeam: '',
    jdProjectName: '',
    jdLocation: '',
    jdEducation: '',
    jdExperienceYears: '',
    jdCompany: '',
    jdRequiredSkills: [],
    jdDesiredSkills: [],
    jdSoftSkills: [],
    jdResponsibilities: [],
    
    // Analysis status and metadata
    jdAnalysisStatus: '',
    jobDescriptionFile: null,
    
    // Timestamps (read-only display)
    createdAt: '',
    updatedAt: '',
    
    // Consultants (for display)
    consultants: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  // Input states for adding skills/responsibilities
  const [responsibilityInput, setResponsibilityInput] = useState('');
  // Order clients input states
  const [orderClientInput, setOrderClientInput] = useState('');
  const [orderClientEmailInput, setOrderClientEmailInput] = useState('');

  // Add these new state variables after existing state declarations
  const [salesUsers, setSalesUsers] = useState([]);
  const [loadingSalesUsers, setLoadingSalesUsers] = useState(false);
  const [updatingCandidates, setUpdatingCandidates] = useState(false);
  const [updatingConsultants, setUpdatingConsultants] = useState(false);

  // Add this constant after the existing constants
  const STATUS_HIERARCHY = {
    'NO_CANDIDATE': 0,
    'CANDIDATE_IDENTIFIED': 1,
    'CANDIDATE_SENT': 2,
    'CANDIDATE_INTERVIEW': 3,
    'GO_NOGO': 4,
    'SALE': 5,
    'CANCELED': 6,
    'DELETED': 7
  };

  // ✅ NEW: Add validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);

  // ✅ NEW: Validation rules based on backend schemas
  const validationRules = {
    title: {
      required: true,
      minLength: 1,
      maxLength: 200,
      message: "Title is required and must be between 1-200 characters"
    },
    client: {
      required: true,
      minLength: 1,
      maxLength: 100,
      message: "Client is required and must be between 1-100 characters"
    },
    description: {
      maxLength: 2000,
      message: "Description must be less than 2000 characters"
    },
    category: {
      maxLength: 100,
      message: "Category must be less than 100 characters"
    },
    quantity: {
      min: 1,
      message: "Quantity must be at least 1"
    },
    progressPct: {
      min: 0,
      max: 100,
      message: "Progress must be between 0-100"
    },
    tjm: {
      min: 0,
      message: "TJM must be greater than or equal to 0"
    },
    priority: {
      required: true,
      enum: ['P1', 'P2', 'P3'],
      message: "Priority must be P1, P2, or P3"
    },
    column: {
      required: true,
      enum: ['NO_CANDIDATE', 'CANDIDATE_IDENTIFIED', 'CANDIDATE_SENT', 'CANDIDATE_INTERVIEW', 'GO_NOGO', 'SALE', 'FROZEN', 'PUSH', 'CANCELED', 'DELETED'],
      message: "Invalid column selection"
    }
  };

  // ✅ NEW: Client-side validation function
  const validateField = (fieldName, value) => {
    const rule = validationRules[fieldName];
    if (!rule) return null;

    // Check required fields
    if (rule.required && (!value || (typeof value === 'string' && !value.trim()))) {
      return rule.message;
    }

    // Skip further validation if field is empty and not required
    if (!value && !rule.required) return null;

    // String validations
    if (typeof value === 'string') {
      if (rule.minLength && value.length < rule.minLength) {
        return rule.message;
      }
      if (rule.maxLength && value.length > rule.maxLength) {
        return rule.message;
      }
    }

    // Number validations
    if (typeof value === 'number' || !Number.isNaN(Number.parseFloat(value))) {
      const numValue = typeof value === 'number' ? value : Number.parseFloat(value);
      if (rule.min !== undefined && numValue < rule.min) {
        return rule.message;
      }
      if (rule.max !== undefined && numValue > rule.max) {
        return rule.message;
      }
    }

    // Enum validations
    if (rule.enum && !rule.enum.includes(value)) {
      return rule.message;
    }

    return null;
  };

  // ✅ NEW: Validate all fields
  const validateForm = () => {
    const errors = {};
    
    // Validate required and constrained fields
    Object.keys(validationRules).forEach(fieldName => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        errors[fieldName] = error;
      }
    });

    // Special validation for TJM in create mode (must be > 0)
    if (!need && formData.tjm !== undefined && formData.tjm !== null && formData.tjm !== '') {
      const tjmValue = Number.parseFloat(formData.tjm);
      if (Number.isNaN(tjmValue) || tjmValue <= 0) {
        errors.tjm = "TJM must be greater than 0";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper function to safely parse arrays
  const parseArrayField = useCallback((field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  // Helper function to get field value with fallback to jdAnalysis
  const getFieldValue = useCallback((primaryField, analysisField, defaultValue = '') => {
    if (primaryField !== undefined && primaryField !== null && primaryField !== '') {
      return primaryField;
    }
    if (need?.jdAnalysis && analysisField && need.jdAnalysis[analysisField]) {
      return need.jdAnalysis[analysisField];
    }
    return defaultValue;
  }, [need]);

  // Helper function to get array field with fallback to jdAnalysis
  const getArrayFieldValue = useCallback((primaryField, analysisField) => {
    const primary = parseArrayField(primaryField);
    if (primary.length > 0) return primary;
    
    if (need?.jdAnalysis && analysisField) {
      return parseArrayField(need.jdAnalysis[analysisField]);
    }
    return [];
  }, [need, parseArrayField]);

  // Update the loadSalesUsers function to add better error handling and logging
  const loadSalesUsers = useCallback(async () => {
    try {
      setLoadingSalesUsers(true);
      console.log('🔧 EditNeedModal - Loading sales users...');
      
      const response = await usersApi.listSalesUsers();
      console.log('🔧 EditNeedModal - Sales users API response:', response);
      
      const users = response.items || [];
      setSalesUsers(users);
      console.log('🔧 EditNeedModal - Loaded sales users:', users.map(u => ({ id: u.id, name: u.name, role: u.role })));
      
      if (users.length === 0) {
        console.warn('🔧 EditNeedModal - No sales users found in response');
      }
    } catch (error) {
      console.error('🔧 EditNeedModal - Error loading sales users:', error);
      setSalesUsers([]);
    } finally {
      setLoadingSalesUsers(false);
    }
  }, []);

  // Add this function after loadSalesUsers
  const updateAdvancedCandidates = useCallback(async (needId, newNeedStatus) => {
    try {
      setUpdatingCandidates(true);
      
      // Get all candidates for this need
      const candidates = await candidatesApi.listNeedCandidates(needId);
      
      const newStatusLevel = STATUS_HIERARCHY[newNeedStatus] || 0;
      const candidatesToUpdate = [];
      
      // Check each candidate's current status
      for (const candidate of candidates) {
        try {
          const latestStep = await candidatesApi.getLatestStep(needId, candidate.id);
          const currentStatusLevel = STATUS_HIERARCHY[latestStep.type] || 0;
          
          // Only update if candidate is more advanced than new need status
          if (currentStatusLevel > newStatusLevel) {
            candidatesToUpdate.push({
              candidateId: candidate.id,
              candidateName: candidate.name,
              currentStatus: latestStep.type,
              newStatus: newNeedStatus
            });
          }
        } catch (error) {
          console.error(`Error getting step for candidate ${candidate.id}:`, error);
        }
      }
      
      // Update candidates that need to be updated
      if (candidatesToUpdate.length > 0) {
        console.log('Updating candidates:', candidatesToUpdate);
        
        for (const update of candidatesToUpdate) {
          try {
            // Create a new step for the candidate with the new status
            const createdStep = await candidatesApi.createStep(needId, update.candidateId, {
              type: update.newStatus,
              status: update.newStatus,
              comment: `Status updated to match need status change`,
              date: new Date().toISOString()
            });
            
            // ✅ FIX: Update store cache immediately
            addStepToCache(needId, update.candidateId, normalizeStep(createdStep));
          } catch (error) {
            console.error(`Error updating candidate ${update.candidateId}:`, error);
          }
        }
        
        return candidatesToUpdate;
      }
      
      return [];
    } catch (error) {
      console.error('Error updating advanced candidates:', error);
      throw error;
    } finally {
      setUpdatingCandidates(false);
    }
  }, [STATUS_HIERARCHY]);

  // Always call hooks at the top level
  const { triggerWorkflow, isLoading } = useWorkflowTrigger(need, onSuccess);
  const { addStepToCache } = useSofteamboardStore();

  useEffect(() => {
    if (need && open) {
      console.log('🔧 EditNeedModal - Populating form with need data:', {
        id: need.id,
        hasJdAnalysis: !!need.jdAnalysis,
        jdAnalysisStatus: need.jdAnalysisStatus,
        orderClients: need.orderClients,
        orderClientsEmail: need.orderClientsEmail,
        // ✅ UPDATED: Log opportunity URL instead of ID
        opportunitySalesforceUrl: need.opportunitySalesforceUrl,
        missionSalesforceUrl: need.missionSalesforceUrl,
      });

      setFormData({
        // Basic information
        title: need.title || '',
        description: need.description || '',
        category: need.category || '',
        quantity: need.quantity || 1,
        client: need.client || '',
        priority: need.priority || 'P2',
        column: need.column || need.status || 'NO_CANDIDATE',
        progressPct: need.progressPct || 0,
        
        tjm: need.tjm ?? '',
        
        // ✅ UPDATED: Use opportunity URL instead of ID
        opportunitySalesforceUrl: need.opportunitySalesforceUrl ?? '',
        missionSalesforceUrl: need.missionSalesforceUrl ?? '',
        
        // Order clients
        orderClients: need.orderClients || [],
        orderClientsEmail: need.orderClientsEmail || [],
        
        // JD Analysis fields - use individual fields first, fallback to jdAnalysis
        jdSeniority: getFieldValue(need.jdSeniority, 'seniority'),
        jdTeam: getFieldValue(need.jdTeam, 'team'),
        jdProjectName: getFieldValue(need.jdProjectName, 'projectname'),
        jdLocation: getFieldValue(need.jdLocation, 'location'),
        jdEducation: getFieldValue(need.jdEducation, 'education'),
        jdExperienceYears: getFieldValue(need.jdExperienceYears, 'experienceyears'),
        jdCompany: getFieldValue(need.jdCompany, 'company') || need.client || '',
        
        // Array fields - use individual fields first, fallback to jdAnalysis
        jdRequiredSkills: getArrayFieldValue(need.jdRequiredSkills, 'requiredskills'),
        jdDesiredSkills: getArrayFieldValue(need.jdDesiredSkills, 'desiredskills'),
        jdSoftSkills: getArrayFieldValue(need.jdSoftSkills, 'soft_skills'),
        jdResponsibilities: getArrayFieldValue(need.jdResponsibilities, 'responsibilities'),
        
        // Metadata
        jdAnalysisStatus: need.jdAnalysisStatus || '',
        jobDescriptionFile: need.jobDescriptionFile || null,
        createdAt: need.createdAt || '',
        updatedAt: need.updatedAt || '',
        consultants: need.consultants || []
      });
      setError('');

      // Load sales users
      loadSalesUsers();
    }
  }, [need, open, getFieldValue, getArrayFieldValue, loadSalesUsers]);

  // ✅ ENHANCED: Input change handler with validation
  const handleInputChange = (field, value) => {
    // Update form data
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Validate field in real-time for better UX
    const error = validateField(field, value);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }

    // Validate Salesforce URLs
    if (field === 'missionSalesforceUrl' && value) {
      const trimmedValue = value.trim();
      if (trimmedValue && !trimmedValue.includes('lightning.force.com')) {
        console.warn('🔧 Mission URL validation: URL should contain lightning.force.com');
      }
      console.log('🔧 Mission URL input change:', { 
        field, 
        value: trimmedValue, 
        isValid: trimmedValue.includes('lightning.force.com') 
      });
    }
  };

  const handleAddArrayItem = (field, input, setInput) => {
    const item = input.trim();
    if (item && !formData[field].includes(item)) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], item]
      }));
      setInput('');
    }
  };

  const handleRemoveArrayItem = (field, itemToRemove) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(item => item !== itemToRemove)
    }));
  };

  const handleKeyPress = (e, field, input, setInput) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddArrayItem(field, input, setInput);
    }
  };

  // Order clients handlers
  const addOrderClient = () => {
    const clientName = orderClientInput.trim();
    const clientEmail = orderClientEmailInput.trim();
    
    console.log('🔧 EditNeedModal - Adding order client:', { clientName, clientEmail });
    
    if (clientName && !formData.orderClients.includes(clientName)) {
      const newFormData = {
        ...formData,
        orderClients: [...formData.orderClients, clientName],
        orderClientsEmail: [...formData.orderClientsEmail, clientEmail || '']
      };
      
      console.log('🔧 EditNeedModal - Updated order clients:', {
        orderClients: newFormData.orderClients,
        orderClientsEmail: newFormData.orderClientsEmail
      });
      
      setFormData(newFormData);
      setOrderClientInput('');
      setOrderClientEmailInput('');
    } else {
      console.log('🔧 EditNeedModal - Client not added:', { 
        reason: !clientName ? 'Empty name' : 'Already exists',
        existing: formData.orderClients 
      });
    }
  };

  const removeOrderClient = (index) => {
    setFormData(prev => ({
      ...prev,
      orderClients: prev.orderClients.filter((_, i) => i !== index),
      orderClientsEmail: prev.orderClientsEmail.filter((_, i) => i !== index)
    }));
  };

  const handleOrderClientKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOrderClient();
    }
  };

  // ✅ ENHANCED: Submit handler with validation
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (formData.isSubmitting) {
      return;
    }

    setIsValidating(true);
    
    // Validate form before submission
    if (!validateForm()) {
      setIsValidating(false);
      setError('Please fix the validation errors before submitting.');
      return;
    }

    setFormData(prev => ({ ...prev, isSubmitting: true }));
    
    try {
      // Check if status changed
      const statusChanged = formData.column !== (need.column || need.status);
      const oldStatus = need.column || need.status;
      const newStatus = formData.column;

      // Prepare data for general API update (exclude column)
      const submitData = {
        title: formData.title?.trim(),
        description: formData.description?.trim() || null,
        category: formData.category?.trim() || null,
        quantity: formData.quantity || 1,
        client: formData.client?.trim(),
        priority: formData.priority,
        progressPct: formData.progressPct,
        consultants: formData.consultants,
        
        tjm: formData.tjm ? Number.parseFloat(formData.tjm) : null,
        
        // Salesforce URLs
        opportunitySalesforceUrl: formData.opportunitySalesforceUrl?.trim() || null,
        missionSalesforceUrl: formData.missionSalesforceUrl?.trim() || null,
        
        // Order clients
        orderClients: formData.orderClients,
        orderClientsEmail: formData.orderClientsEmail,
        
        // JD Analysis individual fields
        jdSeniority: formData.jdSeniority?.trim() || null,
        jdTeam: formData.jdTeam?.trim() || null,
        jdProjectName: formData.jdProjectName?.trim() || null,
        jdLocation: formData.jdLocation?.trim() || null,
        jdEducation: formData.jdEducation?.trim() || null,
        jdExperienceYears: formData.jdExperienceYears?.trim() || null,
        jdCompany: formData.jdCompany?.trim() || null,
        
        // Array fields
        jdRequiredSkills: formData.jdRequiredSkills,
        jdDesiredSkills: formData.jdDesiredSkills,
        jdSoftSkills: formData.jdSoftSkills,
        jdResponsibilities: formData.jdResponsibilities
      };

      console.log('🚀 EditNeedModal - Complete submit data:', submitData);

      // Update general fields first
      let updatedNeed = await needsApi.updateNeed(need.id, submitData);
      
      // Handle column update separately if status changed
      if (statusChanged) {
        console.log('🔄 EditNeedModal - Status changed, updating column:', { oldStatus, newStatus });
        updatedNeed = await needsApi.moveNeedColumn(need.id, newStatus);
        
        // Update advanced candidates after status change
        try {
          const updatedCandidates = await updateAdvancedCandidates(need.id, newStatus);
          if (updatedCandidates.length > 0) {
            console.log(`Updated ${updatedCandidates.length} candidates to match new need status`);
          }
        } catch (candidateError) {
          console.error('Error updating candidates:', candidateError);
          // Don't fail the whole operation if candidate updates fail
        }
      }
      
      onSuccess?.(updatedNeed);
      onClose();
    } catch (err) {
      console.error('Error updating need:', err);
      
      // ✅ ENHANCED: Handle specific API errors
      let errorMessage = 'Failed to update need';
      
      if (err.response?.status === 422) {
        // Handle validation errors from backend
        const detail = err.response.data?.detail;
        if (detail === 'Title cannot be empty') {
          setValidationErrors(prev => ({ ...prev, title: 'Title cannot be empty' }));
          errorMessage = 'Title is required';
        } else if (detail === 'Client cannot be empty') {
          setValidationErrors(prev => ({ ...prev, client: 'Client cannot be empty' }));
          errorMessage = 'Client is required';
        } else if (detail === 'No valid updates provided') {
          errorMessage = 'No changes were made to update';
        } else if (detail?.includes('Invalid column')) {
          setValidationErrors(prev => ({ ...prev, column: detail }));
          errorMessage = 'Invalid status selection';
        } else {
          errorMessage = detail || 'Validation error occurred';
        }
      } else if (err.response?.status === 404) {
        errorMessage = 'Need not found';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again.';
      } else {
        errorMessage = err.response?.data?.detail || err.message || 'Failed to update need';
      }
      
      setError(errorMessage);
    } finally {
      setFormData(prev => ({ ...prev, isSubmitting: false }));
      setIsValidating(false);
    }
  };

  // ✅ NEW: Helper component for displaying field errors
  const FieldError = ({ field }) => {
    if (!validationErrors[field]) return null;
    
    return (
      <div className="mt-1 flex items-center text-sm text-red-600">
        <AlertCircle size={14} className="mr-1" />
        {validationErrors[field]}
      </div>
    );
  };

  // ✅ NEW: Helper function to get input styling based on validation
  const getInputClassName = (field, baseClassName) => {
    const hasError = validationErrors[field];
    const errorClasses = hasError 
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
      : 'border-gray-300 focus:ring-blue-500 focus:border-transparent';
    
    return `${baseClassName} ${errorClasses}`;
  };

  // Add post-analysis specific UI
  const renderPostAnalysisHeader = () => {
    if (mode !== 'post-analysis') return null;

    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              ✅ AI Analysis Complete - Additional Information Required
            </h4>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The AI has successfully analyzed your job description and extracted key information. 
              To enable automatic Salesforce workflow creation, please provide the following additional details:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {missingFields.filter(f => f.required).map((field, index) => (
                <div key={index} className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">{field.label}</span>
                  <span className="text-xs bg-amber-100 px-2 py-1 rounded">
                    {field.workflow}
                  </span>
                </div>
              ))}
            </div>
            
            <div className="text-sm text-amber-700 bg-amber-100 rounded-lg p-3">
              <span className="font-medium">💡 Why this matters:</span> This information enables automatic creation of Salesforce opportunities, products, and missions, saving you significant time in the recruitment process.
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add field highlighting for missing fields
  const isFieldMissing = (fieldName) => {
    return missingFields.some(f => f.field === fieldName);
  };

  const getFieldClassName = (fieldName, baseClassName) => {
    const hasError = validationErrors[fieldName];
    const isMissing = isFieldMissing(fieldName);
    
    let errorClasses = 'border-gray-300 focus:ring-blue-500 focus:border-transparent';
    
    if (hasError) {
      errorClasses = 'border-red-300 focus:ring-red-500 focus:border-red-500';
    } else if (isMissing) {
      errorClasses = 'border-amber-300 focus:ring-amber-500 focus:border-amber-500 bg-amber-50';
    }
    
    return `${baseClassName} ${errorClasses}`;
  };

  if (!open) return null;

  const priorityOptions = [
    { value: 'P1', label: 'P1 - High Priority', color: 'bg-red-100 text-red-800' },
    { value: 'P2', label: 'P2 - Medium Priority', color: 'bg-orange-100 text-orange-800' },
    { value: 'P3', label: 'P3 - Low Priority', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'P4', label: 'P4 - Very Low Priority', color: 'bg-green-100 text-green-800' }
  ];

  const columnOptions = [
    { value: 'NO_CANDIDATE', label: 'No Candidate' },
    { value: 'CANDIDATE_IDENTIFIED', label: 'Candidate Identified' },
    { value: 'CANDIDATE_SENT', label: 'Candidate Sent' },
    { value: 'CANDIDATE_INTERVIEW', label: 'Candidate Interview' },
    { value: 'GO_NOGO', label: 'Go/NoGo' },
    { value: 'SALE', label: 'Sold' },
    { value: 'FROZEN', label: 'Frozen' },
    { value: 'PUSH', label: 'Push' },
    { value: 'CANCELED', label: 'Canceled' },
    { value: 'DELETED', label: 'Deleted' }
  ];

  const tabs = [
    { id: 'basic', label: 'Informations' },
    { id: 'details', label: 'Poste' },
    { id: 'skills', label: 'Compétences' },
    { id: 'metadata', label: 'Plus' }
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Modifier le besoin"
      subtitle={need?.title || 'Update need information and requirements'}
      icon={FileText}
      maxWidth="max-w-5xl"
      closeDisabled={loading || formData.isSubmitting || isValidating}
    >
        {/* Validation summary */}
        {Object.keys(validationErrors).length > 0 && (
          <div className="flex items-center gap-4 px-6 py-2 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-xs font-medium text-red-600">
                {Object.keys(validationErrors).length} error{Object.keys(validationErrors).length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6">
            <FormSectionTabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={setActiveTab}
              label="Rubriques du besoin"
              idPrefix="edit-need-sections"
            />
            {/* Error display with validation summary */}
            {(error || Object.keys(validationErrors).length > 0) && (
              <Alert variant="error">
                <p className="font-medium">
                  {error || 'Please fix the following errors:'}
                </p>
                {Object.keys(validationErrors).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {Object.entries(validationErrors).map(([field, message]) => (
                      <li key={field} className="flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                        {message}
                      </li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}

            {/* Commercial information tab */}
            {activeTab === 'basic' && (
              <div id="edit-need-sections-basic-panel" role="tabpanel" aria-labelledby="edit-need-sections-basic-tab" className="space-y-6">
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border border-blue-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Building className="mr-2 text-blue-600" size={20} />
                    Informations
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label htmlFor="need-title" className="block text-sm font-medium text-gray-700 mb-2">
                        Titre du besoin <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="need-title"
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className={getInputClassName('title', 'w-full px-4 py-3 border rounded-lg text-lg font-medium')}
                        required
                        placeholder="Ex. Lead Developer Java"
                        maxLength={200}
                      />
                      <FieldError field="title" />
                    </div>

                    <div>
                      <label htmlFor="need-client" className="block text-sm font-medium text-gray-700 mb-2">
                        Client <span aria-hidden="true">*</span>
                      </label>
                      <input
                        id="need-client"
                        type="text"
                        value={formData.client}
                        onChange={(e) => handleInputChange('client', e.target.value)}
                        className={getInputClassName('client', 'w-full px-4 py-3 border rounded-lg')}
                        placeholder="Nom du client"
                        required
                        maxLength={100}
                      />
                      <FieldError field="client" />
                    </div>

                    <div>
                      <label htmlFor="need-company" className="block text-sm font-medium text-gray-700 mb-2">
                        Société
                      </label>
                      <input
                        id="need-company"
                        type="text"
                        value={formData.jdCompany}
                        onChange={(e) => handleInputChange('jdCompany', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Société bénéficiaire"
                      />
                    </div>

                    <div>
                      <label htmlFor="need-priority" className="block text-sm font-medium text-gray-700 mb-2">
                        Priorité <span aria-hidden="true">*</span>
                      </label>
                      <select
                        id="need-priority"
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        className={getInputClassName('priority', 'w-full px-4 py-3 border rounded-lg')}
                        required
                      >
                        {priorityOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <FieldError field="priority" />
                    </div>

                    <div>
                      <label htmlFor="need-tjm" className="block text-sm font-medium text-gray-700 mb-2">
                        TJM (€/jour)
                      </label>
                      <input
                        id="need-tjm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.tjm}
                        onChange={(e) => handleInputChange('tjm', e.target.value)}
                        className={getInputClassName('tjm', 'w-full px-4 py-3 border rounded-lg')}
                        placeholder="Ex. 500"
                      />
                      <FieldError field="tjm" />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="mr-2 text-blue-600" size={20} />
                    Consultants assignés
                  </h3>

                  {loadingSalesUsers ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <Loader className="animate-spin mr-2" size={14} />
                      Chargement des consultants…
                    </div>
                  ) : salesUsers.length === 0 ? (
                    <p className="text-sm text-gray-500 rounded-lg bg-white p-3">Aucun consultant commercial disponible.</p>
                  ) : (
                    <>
                      <label htmlFor="assigned-consultant" className="block text-sm font-medium text-gray-700 mb-2">Ajouter un consultant</label>
                      <select
                        id="assigned-consultant"
                        onChange={(e) => {
                          const userId = e.target.value;
                          if (userId && !formData.consultants.includes(userId)) {
                            handleInputChange('consultants', [...formData.consultants, userId]);
                          }
                          e.target.value = '';
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={loading}
                      >
                        <option value="">Sélectionner un consultant…</option>
                        {salesUsers
                          .filter(user => !formData.consultants.includes(user.id))
                          .map(user => <option key={user.id} value={user.id}>{user.name} ({user.email})</option>)}
                      </select>
                      {formData.consultants.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2" aria-label="Consultants sélectionnés">
                          {formData.consultants.map(consultantId => {
                            const consultant = salesUsers.find(user => user.id === consultantId);
                            return (
                              <span key={consultantId} className="inline-flex min-h-[44px] items-center rounded-full border border-blue-200 bg-blue-100 px-3 text-sm text-blue-800">
                                {consultant?.name || `Consultant inconnu (${consultantId.substring(0, 8)}…)`}
                                <button
                                  type="button"
                                  disabled={loading}
                                  onClick={() => handleInputChange('consultants', formData.consultants.filter(id => id !== consultantId))}
                                  className="ml-2 min-h-[32px] min-w-[32px] text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                  aria-label={`Retirer ${consultant?.name || 'ce consultant'}`}
                                >
                                  <X size={12} aria-hidden="true" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-blue-50 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="mr-2 text-indigo-600" size={20} />
                    Contacts de commande
                    <span className="ml-2 bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded-full">
                      {formData.orderClients.length} contact{formData.orderClients.length !== 1 ? 's' : ''}
                    </span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="order-client-name" className="block text-sm font-medium text-gray-700 mb-2">Nom du contact</label>
                      <input
                        id="order-client-name"
                        type="text"
                        value={orderClientInput}
                        onChange={(e) => setOrderClientInput(e.target.value)}
                        onKeyPress={handleOrderClientKeyPress}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Ex. Jean Dupont"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label htmlFor="order-client-email" className="block text-sm font-medium text-gray-700 mb-2">Email du contact <span className="text-gray-400">(optionnel)</span></label>
                      <input
                        id="order-client-email"
                        type="email"
                        value={orderClientEmailInput}
                        onChange={(e) => setOrderClientEmailInput(e.target.value)}
                        onKeyPress={handleOrderClientKeyPress}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Ex. jean.dupont@entreprise.com"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={addOrderClient}
                    disabled={!orderClientInput.trim() || loading}
                    className="w-full min-h-[44px] px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4"
                  >
                    <Plus size={16} className="inline mr-2" aria-hidden="true" />
                    Ajouter un contact de commande
                  </button>

                  {formData.orderClients.length > 0 && (
                    <div className="space-y-3">
                      {formData.orderClients.map((client, index) => (
                        <div key={`${client}-${index}`} className="flex items-center justify-between bg-white p-4 rounded-lg border border-indigo-100 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white font-semibold" aria-hidden="true">
                              {client.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{client}</div>
                              <div className="text-sm text-gray-600">{formData.orderClientsEmail[index] || 'Email non renseigné'}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {formData.orderClientsEmail[index] && (
                              <button
                                type="button"
                                onClick={() => window.open(`mailto:${formData.orderClientsEmail[index]}`, '_blank')}
                                className="min-h-[44px] min-w-[44px] text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-full transition-colors"
                                aria-label={`Écrire à ${formData.orderClientsEmail[index]}`}
                              >
                                <ExternalLink className="w-4 h-4 mx-auto" aria-hidden="true" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeOrderClient(index)}
                              className="min-h-[44px] min-w-[44px] text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full transition-colors"
                              disabled={loading}
                              aria-label={`Retirer ${client}`}
                            >
                              <Trash2 size={16} className="mx-auto" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Job Details Tab */}
            {activeTab === 'details' && (
              <div id="edit-need-sections-details-panel" role="tabpanel" aria-labelledby="edit-need-sections-details-tab" className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Target className="mr-2 text-blue-600" size={20} />
                    Poste
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="md:col-span-2">
                      <label htmlFor="need-description" className="block text-sm font-medium text-gray-700 mb-2">Description du poste</label>
                      <textarea
                        id="need-description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows={6}
                        className={getInputClassName('description', 'w-full px-4 py-3 border rounded-lg')}
                        placeholder="Description détaillée du poste et de ses enjeux…"
                        maxLength={2000}
                      />
                      <div className="mt-1 text-xs text-gray-500">{formData.description?.length || 0}/2000 caractères</div>
                      <FieldError field="description" />
                    </div>

                    <div>
                      <label htmlFor="need-category" className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                      <select
                        id="need-category"
                        value={formData.category || ''}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className={getInputClassName('category', 'w-full px-4 py-3 border rounded-lg')}
                      >
                        <option value="">Sélectionner une catégorie…</option>
                        <option value="developer">Développement</option>
                        <option value="test">Test</option>
                        <option value="functionnal">Fonctionnel</option>
                        <option value="IT">IT</option>
                        <option value="devops">DevOps</option>
                        <option value="others">Autre</option>
                      </select>
                      <FieldError field="category" />
                    </div>

                    <div>
                      <label htmlFor="need-quantity" className="block text-sm font-medium text-gray-700 mb-2">Quantité</label>
                      <input
                        id="need-quantity"
                        type="number"
                        min="1"
                        value={formData.quantity}
                        onChange={(e) => handleInputChange('quantity', Number.parseInt(e.target.value) || 1)}
                        className={getInputClassName('quantity', 'w-full px-4 py-3 border rounded-lg')}
                        placeholder="1"
                      />
                      <FieldError field="quantity" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Clock className="mr-1" size={14} />
                        Seniority Level
                      </label>
                      <input
                        type="text"
                        value={formData.jdSeniority}
                        onChange={(e) => handleInputChange('jdSeniority', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Senior, de 2 à 6 ans"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Calendar className="mr-1" size={14} />
                        Experience Years
                      </label>
                      <input
                        type="text"
                        value={formData.jdExperienceYears}
                        onChange={(e) => handleInputChange('jdExperienceYears', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 2, 5-7"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <GraduationCap className="mr-1" size={14} />
                        Education
                      </label>
                      <input
                        type="text"
                        value={formData.jdEducation}
                        onChange={(e) => handleInputChange('jdEducation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., BAC + 3 en informatique"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <MapPin className="mr-1" size={14} />
                        Location
                      </label>
                      <input
                        type="text"
                        value={formData.jdLocation}
                        onChange={(e) => handleInputChange('jdLocation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Paris, Remote"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Users className="mr-1" size={14} />
                        Team
                      </label>
                      <input
                        type="text"
                        value={formData.jdTeam}
                        onChange={(e) => handleInputChange('jdTeam', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., équipe de 7 personnes"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Briefcase className="mr-1" size={14} />
                        Project Name
                      </label>
                      <input
                        type="text"
                        value={formData.jdProjectName}
                        onChange={(e) => handleInputChange('jdProjectName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., jedeclare.com"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Skills & Requirements Tab */}
            {activeTab === 'skills' && (
              <div id="edit-need-sections-skills-panel" role="tabpanel" aria-labelledby="edit-need-sections-skills-tab" className="space-y-6">
                {/* Required Skills */}
                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border border-red-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Star className="mr-2 text-red-500" size={20} />
                    Required Skills
                    <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                      {formData.jdRequiredSkills.length} skills
                    </span>
                  </h4>
                  
                  <TagInput
                    items={formData.jdRequiredSkills}
                    onAdd={(value) => setFormData(prev => ({ ...prev, jdRequiredSkills: [...prev.jdRequiredSkills, value] }))}
                    onRemove={(value) => handleRemoveArrayItem('jdRequiredSkills', value)}
                    placeholder="Add required skill and press Enter..."
                    color="red"
                    icon={Star}
                  />
                </div>

                {/* Desired Skills */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Award className="mr-2 text-green-500" size={20} />
                    Desired Skills
                    <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                      {formData.jdDesiredSkills.length} skills
                    </span>
                  </h4>
                  
                  <TagInput
                    items={formData.jdDesiredSkills}
                    onAdd={(value) => setFormData(prev => ({ ...prev, jdDesiredSkills: [...prev.jdDesiredSkills, value] }))}
                    onRemove={(value) => handleRemoveArrayItem('jdDesiredSkills', value)}
                    placeholder="Add desired skill and press Enter..."
                    color="green"
                    icon={Award}
                  />
                </div>

                {/* Soft Skills */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <UserCheck className="mr-2 text-purple-500" size={20} />
                    Soft Skills
                    <span className="ml-2 bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
                      {formData.jdSoftSkills.length} skills
                    </span>
                  </h4>
                  
                  <TagInput
                    items={formData.jdSoftSkills}
                    onAdd={(value) => setFormData(prev => ({ ...prev, jdSoftSkills: [...prev.jdSoftSkills, value] }))}
                    onRemove={(value) => handleRemoveArrayItem('jdSoftSkills', value)}
                    placeholder="Add soft skill and press Enter..."
                    color="purple"
                    icon={UserCheck}
                  />
                </div>

                {/* Responsibilities */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="mr-2 text-blue-500" size={20} />
                    Key Responsibilities
                    <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                      {formData.jdResponsibilities.length} tasks
                    </span>
                  </h4>
                  
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={responsibilityInput}
                      onChange={(e) => setResponsibilityInput(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, 'jdResponsibilities', responsibilityInput, setResponsibilityInput)}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add responsibility and press Enter..."
                    />
                    <button
                      type="button"
                      onClick={() => handleAddArrayItem('jdResponsibilities', responsibilityInput, setResponsibilityInput)}
                      className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {formData.jdResponsibilities.map((responsibility, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-100">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-xs font-bold text-white">{index + 1}</span>
                        </div>
                        <span className="flex-1 text-sm text-gray-700">{responsibility}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveArrayItem('jdResponsibilities', responsibility)}
                          className="text-red-600 hover:text-red-800 flex-shrink-0"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Metadata Tab */}
            {activeTab === 'metadata' && (
              <div id="edit-need-sections-metadata-panel" role="tabpanel" aria-labelledby="edit-need-sections-metadata-tab" className="space-y-6">
                <div className="rounded-xl border border-orange-100 bg-gradient-to-br from-orange-50 to-amber-50 p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <ExternalLink className="mr-2 text-orange-600" size={20} />
                    Intégrations
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="opportunity-salesforce-url" className="block text-sm font-medium text-gray-700 mb-2">Opportunité Salesforce</label>
                      <div className="flex">
                        <input
                          id="opportunity-salesforce-url"
                          type="url"
                          value={formData.opportunitySalesforceUrl || ''}
                          onChange={(e) => handleInputChange('opportunitySalesforceUrl', e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="https://…lightning.force.com/…"
                        />
                        {!formData.opportunitySalesforceUrl ? (
                          <button
                            type="button"
                            onClick={() => triggerWorkflow('opportunity')}
                            disabled={isLoading('opportunity')}
                            className="min-h-[44px] min-w-[44px] bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors"
                            aria-label="Créer l’opportunité Salesforce"
                          >
                            {isLoading('opportunity') ? <Loader2 className="w-4 h-4 animate-spin mx-auto" aria-hidden="true" /> : <Zap className="w-4 h-4 mx-auto" aria-hidden="true" />}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => window.open(formData.opportunitySalesforceUrl, '_blank')}
                            className="min-h-[44px] min-w-[44px] bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors"
                            aria-label="Ouvrir l’opportunité dans Salesforce"
                          >
                            <ExternalLink className="w-4 h-4 mx-auto" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="mission-salesforce-url" className="block text-sm font-medium text-gray-700 mb-2">Mission Salesforce</label>
                      <div className="flex">
                        <input
                          id="mission-salesforce-url"
                          type="url"
                          value={formData.missionSalesforceUrl || ''}
                          onChange={(e) => handleInputChange('missionSalesforceUrl', e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="https://…lightning.force.com/…"
                        />
                        {!formData.missionSalesforceUrl ? (
                          <button
                            type="button"
                            onClick={() => triggerWorkflow('mission')}
                            disabled={isLoading('mission')}
                            className="min-h-[44px] min-w-[44px] bg-orange-600 text-white rounded-r-lg hover:bg-orange-700 transition-colors"
                            aria-label="Créer la mission Salesforce"
                          >
                            {isLoading('mission') ? <Loader2 className="w-4 h-4 animate-spin mx-auto" aria-hidden="true" /> : <Zap className="w-4 h-4 mx-auto" aria-hidden="true" />}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => window.open(formData.missionSalesforceUrl, '_blank')}
                            className="min-h-[44px] min-w-[44px] bg-orange-600 text-white rounded-r-lg hover:bg-orange-700 transition-colors"
                            aria-label="Ouvrir la mission dans Salesforce"
                          >
                            <ExternalLink className="w-4 h-4 mx-auto" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <FileText className="mr-2 text-gray-600" size={20} />
                    Métadonnées Softeamia
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Status Dropdown */}
                    <div className="bg-white p-4 rounded-lg border">
                      <label htmlFor="need-column" className="block text-sm font-medium text-gray-700 mb-2">
                        État du pipeline
                      </label>
                      <select
                        id="need-column"
                        value={formData.column}
                        onChange={(e) => handleInputChange('column', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {columnOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {updatingCandidates && (
                        <div className="mt-2 flex items-center text-sm text-blue-600">
                          <Loader className="animate-spin mr-1" size={12} />
                          Mise à jour des candidats…
                        </div>
                      )}
                    </div>

                    <div className="bg-white p-4 rounded-lg border">
                      <label htmlFor="need-progress" className="block text-sm font-medium text-gray-700 mb-2">Progression (%)</label>
                      <input
                        id="need-progress"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.progressPct}
                        onChange={(e) => handleInputChange('progressPct', Number.parseInt(e.target.value) || 0)}
                        className={getInputClassName('progressPct', 'w-full px-3 py-2 border rounded-lg')}
                      />
                      <FieldError field="progressPct" />
                    </div>

                    {/* Analysis Status */}
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <Brain className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-900">Analyse de la fiche</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${
                          formData.jdAnalysisStatus === 'READY' ? 'bg-green-400' :
                          formData.jdAnalysisStatus === 'PENDING' ? 'bg-yellow-400' :
                          'bg-gray-400'
                        }`} />
                        <span className="text-sm text-gray-600">
                          {formData.jdAnalysisStatus || 'Non analysée'}
                        </span>
                      </div>
                    </div>

                    {/* Created Date */}
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-5 h-5 text-purple-600" />
                        <span className="font-medium text-gray-900">Créé le</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {formData.createdAt ? new Date(formData.createdAt).toLocaleString() : 'Inconnu'}
                      </span>
                    </div>

                    {/* Updated Date */}
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-orange-600" />
                        <span className="font-medium text-gray-900">Dernière mise à jour</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {formData.updatedAt ? new Date(formData.updatedAt).toLocaleString() : 'Inconnue'}
                      </span>
                    </div>

                    {/* Analysis URLs */}
                    {need?.jdAnalysis?.jd_analysis_s3_url && (
                      <div className="md:col-span-2 bg-white p-4 rounded-lg border">
                        <div className="flex items-center gap-2 mb-2">
                          <Download className="w-5 h-5 text-indigo-600" />
                          <span className="font-medium text-gray-900">Analysis Files</span>
                        </div>
                        <div className="flex gap-4">
                          {need.jdAnalysis.jd_s3_url && (
                            <a 
                              href={need.jdAnalysis.jd_s3_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                            >
                              <FileText className="w-4 h-4" />
                              Original JD
                            </a>
                          )}
                          <a 
                            href={need.jdAnalysis.jd_analysis_s3_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          >
                            <Brain className="w-4 h-4" />
                            Analysis JSON
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ✅ NEW: Salesforce Integration Requirements Alert */}
            {activeTab === 'metadata' && (!formData.orderClients || formData.orderClients.length === 0 || !formData.tjm || formData.tjm <= 0) && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      Salesforce Integration Requirements
                    </h4>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      To enable automatic Salesforce opportunity and product creation, please ensure the following information is provided:
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div className={`flex items-center gap-2 ${(!formData.orderClients || formData.orderClients.length === 0) ? 'text-amber-700' : 'text-green-700'}`}>
                        {(!formData.orderClients || formData.orderClients.length === 0) ? (
                          <AlertTriangle className="w-4 h-4" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        <span className="font-medium">Order Client Contacts</span>
                      </div>
                      
                      <div className={`flex items-center gap-2 ${(!formData.tjm || formData.tjm <= 0) ? 'text-amber-700' : 'text-green-700'}`}>
                        {(!formData.tjm || formData.tjm <= 0) ? (
                          <AlertTriangle className="w-4 h-4" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        <span className="font-medium">TJM (Daily Rate)</span>
                      </div>
                    </div>
                    
                    <div className="text-sm text-blue-700 bg-blue-100 rounded-lg p-3">
                      <span className="font-medium">💡 Tip:</span> Once this information is added and saved, you'll be able to automatically create Salesforce opportunities and products using the RPA workflow buttons.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form Actions */}
            <ModalActions
              onCancel={onClose}
              loading={loading || formData.isSubmitting || isValidating}
              submitLabel="Update Need"
              loadingLabel={isValidating ? 'Validating...' : 'Updating...'}
              submitIcon={Save}
              disabled={Object.keys(validationErrors).length > 0}
            />
          </form>
    </Modal>
  );
};

export default EditNeedModal;
